import Cart from 'cart-store';

// Kept separate from Venue's recommendation element so other theme areas are unaffected.
class CduCartRecommendations extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute('section-id');
    this.container = this.querySelector('[cart-recommendations-content]');
    this.signature = null;
    this.revision = (this.revision || 0) + 1;
    this.unsubscribe = Cart.subscribe(state => this.schedule(state));
    this.schedule(Cart.getState());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    clearTimeout(this.timer);
    this.controller?.abort();
    this.revision += 1;
    this.signature = null;
    this.clearCards();
  }

  schedule(state) {
    const items = state.cart?.items || [];
    const signature = JSON.stringify(items.map(item => [item.key, item.product_id, item.quantity]));
    if (signature === this.signature) return;
    this.signature = signature;
    this.revision += 1;
    this.controller?.abort();
    clearTimeout(this.timer);
    // Do not leave outdated suggestions visible while cart contents change.
    this.setAttribute('hidden', '');
    if (!items.length) {
      this.clearCards();
      return;
    }
    const revision = this.revision;
    this.timer = setTimeout(() => this.load(revision), 100);
  }

  async load(revision) {
    const controller = new AbortController();
    this.controller = controller;
    try {
      const root = window.Shopify?.routes?.root || '/';
      // Retain the current page query, including an unpublished theme's preview parameters.
      const url = new URL(window.location.href);
      url.pathname = `${root}cart`;
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.getAttribute('data-section'));
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) throw new Error('Recommendation section unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = html.querySelector('[data-cdu-cart-recommendation-data]');
      if (!data) throw new Error('Recommendation data missing');
      const entries = JSON.parse(data.textContent);
      if (!Array.isArray(entries)) throw new Error('Invalid recommendation data');
      if (revision !== this.revision || !this.isConnected) return;
      const inCart = new Set((Cart.getState().cart?.items || []).map(item => String(item.product_id)));
      const seen = new Set();
      const products = entries.reduce((result, entry) => {
        const product = entry.product;
        const id = String(product?.id);
        if (product?.available && entry.url && !inCart.has(id) && !seen.has(id)) {
          seen.add(id);
          result.push({ ...product, url: entry.url });
        }
        return result;
      }, []).slice(0, 2);
      this.renderCards(products);
    } catch (error) {
      if (error.name === 'AbortError' || revision !== this.revision) return;
      this.setAttribute('hidden', '');
      this.clearCards();
      console.warn('CDU cart suggestions could not be loaded.', error);
      // Permit a retry on the next cart state change.
      this.signature = null;
    }
  }

  clearCards() {
    if (!this.container) return;
    // Venue moves quick-shop dialogs outside the card; remove those with their old cards.
    for (const card of this.container.children) {
      if (card.quickShop && !card.contains(card.quickShop)) card.quickShop.remove();
    }
    this.container.replaceChildren();
  }

  movePriceIntoButton(card) {
    const price = card.querySelector('product-card-price');
    const button = card.querySelector('product-card-button button:not([disabled])');
    // Keep the standalone price when quick shop / add buttons are disabled in the theme.
    if (!price || !button || button.contains(price)) return;
    const label = button.querySelector('staged-action-text') || button;
    if (button.hasAttribute('data-trigger-quick-shop')) label.textContent = 'Choose options';
    const separator = document.createElement('span');
    separator.className = 'cdu-cart-button-separator';
    separator.textContent = '·';
    separator.style.setProperty('display', 'inline-block', 'important');
    separator.style.setProperty('padding-left', '0.4em', 'important');
    separator.style.setProperty('padding-right', '0.4em', 'important');
    separator.style.setProperty('flex-shrink', '0');
    label.append(separator, price);
    card.classList.add('cdu-cart-mini--price-in-button');
  }

  renderCards(products) {
    const template = document.getElementById(`${this.sectionId}--template-cart-recommendations-card`);
    if (!template) throw new Error('Recommendation card template missing');
    const existing = new Map(Array.from(this.container.children).map(card => [String(card.product?.id), card]));
    const retained = new Set();
    const cards = products.map(product => {
      const key = JSON.stringify(product);
      const previous = existing.get(String(product.id));
      if (previous?.cduProductKey === key) {
        retained.add(previous);
        return previous;
      }
      const card = template.content.cloneNode(true).firstElementChild;
      card.sectionId = this.sectionId;
      card.cduPendingProduct = product;
      card.cduProductKey = key;
      return card;
    });
    for (const card of Array.from(this.container.children)) {
      if (!retained.has(card)) {
        if (card.quickShop && !card.contains(card.quickShop)) card.quickShop.remove();
        card.remove();
      }
    }
    cards.forEach((card, index) => {
      const atPosition = this.container.children[index];
      if (atPosition !== card) this.container.insertBefore(card, atPosition || null);
      // Venue initializes its image hooks in connectedCallback, before setting product data.
      if (card.cduPendingProduct) {
        card.product = card.cduPendingProduct;
        card.id = `cdu-cart-recommendation--${card.product.id}`;
        delete card.cduPendingProduct;
        this.movePriceIntoButton(card);
        // Venue's mini-card templates hide controls from keyboard focus by default.
        for (const button of card.querySelectorAll('product-card-button button:not([disabled])')) {
          button.removeAttribute('aria-hidden');
          button.removeAttribute('tabindex');
        }
      }
    });
    this.toggleAttribute('hidden', cards.length === 0);
  }
}

if (!customElements.get('cdu-cart-recommendations')) {
  customElements.define('cdu-cart-recommendations', CduCartRecommendations);
}
